// src/data/applicationApproval.js
//
// ONB-3, ONB-4 and ONB-5 (PLAN ONB-1, approved by Bob 2026-09-21 as part of B-5; rulings 193, 196).
// PURE. No Firestore, no clock, no network - the callable supplies both and does the writing.
//
// THE ONE THING THIS MODULE EXISTS TO PREVENT: an approval granting an office.
//
// On this platform a role is a PROJECTION OF AN OFFICE TERM. setOfficeHolder opens the term in
// officeTerms and projects role/committee/scopes into the token; its own header calls it "the only
// thing on earth that writes role/committee/scopes claims", and scripts\set-roles.js has been
// retired to break-glass since 2026-09-14. (PLAN ONB-1 named set-roles.js as the writer to extend.
// That was stale and is corrected here.)
//
// So approval mints `member` AND NOTHING ELSE. A claimed office is recorded as a CLAIM PENDING
// CONFIRMATION and must travel through setOfficeHolder, one rung up, as a separate act by someone
// with authority over that seat. Ruling 193.2: a claimed office is a statement, never a grant.

export const MEMBER_ROLE = 'member';
export const DECISIONS = Object.freeze(['approved', 'refused']);

// Record 612 (Bob 10/8; ruling 291 + decisions list item 10): THE FOUR CASES IN WHICH THE GENERAL CHAIRPERSON DECIDES.
// Ruling 291: "only the routed LC decides". Bob's item 10 named when the committee's General Chairperson does instead:
//   - the application is UNROUTED - no Local was resolved from the posting (routingStatus is not 'routed');
//   - the Local's seat was VACANT when it arrived (routingStatus 'seat_vacant'; ruling 60: a vacant seat's work vests
//     in the GC). The routing is read as written at submission, so a seat filled afterwards does not move it back;
//   - the Local Chairperson REFERRED it to him (the refer act writes referredAt);
//   - it involves a SENIORITY DISPUTE (the Local Chairperson's seniority_dispute act writes seniorityDispute).
// Returns the case in words, or '' when the decision is the routed Local Chairperson's. An application carrying no
// routingStatus is NOT presumed routed. functions\shared\applicationApproval.js is this file's projection.
export function gcDecides(application) {
  const app = application || {};
  if (app.seniorityDispute === true) return 'it involves a seniority dispute';
  if (app.referredAt) return 'the Local Chairperson referred it';
  if (app.routingStatus === 'seat_vacant') return "the Local's Local Chairperson seat is vacant";
  if (app.routingStatus !== 'routed') return 'it is unrouted - no Local was resolved from the posting';
  return '';
}

// ONB-4: WHO MAY DECIDE. A claimed office routes the decision one rung UP from where it would
// otherwise sit. The rungs are the office spine, not the screens.
export function mayDecide(actor, application) {
  const a = actor || {};
  const app = application || {};
  const office = String(app.claimedOffice || 'none');
  if (a.role === 'admin') return { ok: true, rung: 'admin' };

  if (office === 'general_chair') {
    // Ruling 196: confirmed by signed letter of agreement and payment of fees - both OUTSIDE the
    // platform (Bob, 2026-09-21). The platform gates on the administrator's confirmation alone and
    // records no payment, no receipt and no fee.
    return { ok: false, rung: 'admin', reason: 'A claimed General Chairperson is confirmed by the administrator only.' };
  }
  if (office === 'local_chair') {
    const ok = a.role === 'general_chair' && a.committee && a.committee === app.generalCommittee;
    return ok ? { ok: true, rung: 'general_chair' }
              : { ok: false, rung: 'general_chair', reason: "A claimed Local Chairperson is confirmed by his committee's General Chairperson." };
  }
  // 'none' and 'other': RULING 291 (Bob 2026-10-08, Record 612) - ONLY THE ROUTED LOCAL CHAIRPERSON DECIDES, unless
  // gcDecides() names one of the four cases that put it with the General Chairperson of the committee. Until today
  // either of them could. 'other' is NOT treated as an office - it is recorded and grants nothing (ruling 193.2).
  const gcCase = gcDecides(app);
  if (a.role === 'local_chair') {
    const scopes = Array.isArray(a.scopes) ? a.scopes : (a.scopeKey ? [a.scopeKey] : []);
    const ok = !!app.scopeKey && scopes.indexOf(app.scopeKey) !== -1;
    if (!ok) return { ok: false, rung: 'local_chair', reason: 'That application is not in your territory.' };
    if (gcCase) return { ok: false, rung: 'general_chair', reason: 'Your General Chairperson decides this application: ' + gcCase + '. [R-291]' };
    return { ok: true, rung: 'local_chair' };
  }
  if (a.role === 'general_chair') {
    const ok = a.committee && a.committee === app.generalCommittee;
    if (!ok) return { ok: false, rung: 'general_chair', reason: 'That application is not in your committee.' };
    if (!gcCase) {
      return { ok: false, rung: 'local_chair', reason: 'The routed Local Chairperson decides this application. It comes to you if he refers it, if it involves a seniority dispute, or if it is unrouted or his seat is vacant. [R-291]' };
    }
    return { ok: true, rung: 'general_chair' };
  }
  return { ok: false, rung: null, reason: 'Only a Local Chairperson, a General Chairperson or the administrator decides an application.' };
}

// ONB-3: WHAT AN APPROVAL GRANTS. Always and only `member`. Never an office.
export function decisionEffect(application, decision) {
  const app = application || {};
  if (decision !== 'approved') return { grantsRole: null, officeClaimPending: null };
  const office = String(app.claimedOffice || 'none');
  return {
    grantsRole: MEMBER_ROLE,
    committee: String(app.generalCommittee || ''),
    scopeKey: String(app.scopeKey || ''),
    // Recorded, never granted. Cleared only by a separate setOfficeHolder act.
    officeClaimPending: (office === 'none') ? null : office,
  };
}

// ONB-5: THE ONBOARDING RECORD. What was asked, what was answered, WHAT ROSTER IT WAS CHECKED
// AGAINST BY NAME AND DATE, who decided, when, and on a refusal, why.
//
// The roster is recorded even when no check ran. "It was verified" is worthless; "checked against
// YCP_LC_Roster_GO342.csv dated 12 September" is a record, and "no roster held - NOT PERFORMED" is
// also a record. What is never written is silence that reads like a pass (ruling 194).
export function buildOnboardingRecord({ application, actor, decision, reason, rosterCheck, now }) {
  const app = application || {};
  const a = actor || {};
  const eff = decisionEffect(app, decision);
  const rc = rosterCheck || {};
  return {
    applicationId: String(app.id || ''),
    decision,
    decidedBy: String(a.uid || ''),
    decidedByEmail: String(a.email || ''),
    decidedByRole: String(a.role || ''),
    decidedAt: now,
    reason: decision === 'refused' ? String(reason || '') : '',
    // The routing AS AT THE DECISION. Firestore rules cannot join one document to another, so a
    // record that must be readable by exactly the officers who could read the application has to
    // carry the two fields that scope it - the same reason /claimants carries scopeKey and
    // committee. This is not a second writer of the routing (§XI): the application's routing can be
    // re-cut if a committee is reorganised, and WHO DECIDED, UNDER WHICH SEAT must not move when it
    // is. This is the snapshot, like `answers` beside it.
    scopeKey: String(app.scopeKey || ''),
    generalCommittee: String(app.generalCommittee || ''),
    // what was asked and answered, as submitted
    answers: app.answers || null,
    acknowledgements: app.acknowledgements || {},
    accuracyNoticeVersion: String(app.accuracyNoticeVersion || ''),
    // ruling 194: the roster BY NAME AND DATE, or an explicit NOT PERFORMED
    rosterCheckedFile: rc.file ? String(rc.file) : '',
    rosterCheckedDate: rc.date ? String(rc.date) : '',
    rosterCheckResult: rc.performed === true ? String(rc.result || '') : 'NOT PERFORMED',
    // what the decision actually did
    grantedRole: eff.grantsRole,
    officeClaimPending: eff.officeClaimPending,
    claimedOffice: String(app.claimedOffice || 'none'),
  };
}

// A refusal must carry a reason. Ruling 193.3: dated, attributed, and a refusal carries its reason.
export function validateDecision({ decision, reason }) {
  if (DECISIONS.indexOf(decision) === -1) return { ok: false, why: 'decision must be approved or refused' };
  if (decision === 'refused' && !String(reason || '').trim()) return { ok: false, why: 'a refusal must carry its reason' };
  return { ok: true, why: '' };
}
